import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import {
  BuildingThreeIcon,
  CalendarTwoIcon,
  HealthFormIcon,
  PeopleOneOutlineIcon,
  PharmacyIcon,
  PrintIcon,
  TelephoneIcon,
  VaccinationIcon,
  WarningIcon
} from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { getAffectedStaffEnrollments } from '../../../../api/affected-staffs/get-affected-staff-enrollments'
import { API, constructApiUrl } from '../../../../api/apis.config'
import { useDownloadDocuments } from '../../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useSecureFile } from '../../../../lib/hooks/use-secure-file'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  genderEnumValues,
  genderOptionsMapping
} from '../../../../lib/types/enums/common/gender.enum'
import { NoneEnum } from '../../../../lib/types/enums/common/none.enum'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { contactEnumMapping } from '../../../../lib/types/enums/enrollment/contact.enum'
import { MedicalStatusEnum } from '../../../../lib/types/enums/enrollment/medical-status.enum'
import { EnrollmentCompact } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { LocaleKeys } from '../../../../locale/translation-types'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import { EnrollmentBadges } from './utils'

type EnrollmentsCardProps = {
  enrollment: EnrollmentCompact
}

function EnrollmentsCardHeader({ enrollment }: EnrollmentsCardProps) {
  const { modalState, toggleModal } = useModalManager<'enrollment-image', EnrollmentCompact>([
    'enrollment-image'
  ])

  const {
    url,
    isLoading: isPicLoading,
    status: picStatus
  } = useSecureFile(enrollment.secureIdentityPicture?.uploadId)
  const isPicError = picStatus === 'INFECTED' || picStatus === 'FAILED'

  const { t } = useTranslate()
  const birhdate = enrollment.childBirthDate
    ? `${DateFormatter.DDMMYYYY(enrollment.childBirthDate)} - ${EnrollmentProperties.getAgeAtDeparture(enrollment) ?? '-'}`
    : undefined

  function EnrollmentData() {
    return (
      <div className="mobile:flex-col w-full flex flex-row gap-2 justify-between mobile:items-start items-center mobile:justify-start">
        <div className="mobile:flex-col mobile:items-start flex flex-row items-center gap-2">
          <Typography variant="caption">
            {toFullName(enrollment.childFirstName, enrollment.childLastName, {
              options: { mode: 'lastname-firstname' }
            })}
          </Typography>

          <Badge
            size="xsmall"
            variant="danger"
            text={valueRender(enrollment.childGender, (g) => genderOptionsMapping[g].gender)}
          />
        </div>

        <Badge text={`N° ${enrollment.enrollmentNumber}`} size="xsmall" variant="darkBlue" />
      </div>
    )
  }

  return (
    <div className="flex flex-row gap-3 items-start mobile:flex-col">
      <div className="flex flex-row gap-2">
        <div className="size-20 flex items-center justify-center rounded-lg bg-grey-light-hover">
          {isPicLoading ? (
            <Loader size="xs" />
          ) : isPicError ? (
            <WarningIcon color={tailwindColors.secondaryRed.DEFAULT} />
          ) : (
            <img
              src={url ?? '/profile-default.jpg'}
              className="w-full h-full rounded-lg cursor-pointer object-cover object-center"
              onClick={() => toggleModal('enrollment-image', enrollment)}
            />
          )}
        </div>

        <div className="mobile:block hidden">
          <EnrollmentData />
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full">
        <div className="mobile:hidden">
          <EnrollmentData />
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge
            text={`Née le ${birhdate} ans`}
            icon={<CalendarTwoIcon className="w-4 h-4" />}
            iconPosition="start"
            variant="grey"
            size="xsmall"
          />
          <Badge
            text={valueRender(enrollment.childPhoneNumber, (ph) => formatPhoneNumber(ph))}
            icon={<TelephoneIcon className="w-4 h-4" color={tailwindColors.grey.DEFAULT} />}
            iconPosition="start"
            variant="grey"
            size="xsmall"
          />
          <Badge text={`CSE : -`} variant="grey" size="xsmall" />
          <Badge
            text={`Classe : ${valueRender(enrollment.childSchoolLevel, (level) => schoolLevelEnumMapping[level])}`}
            variant="grey"
            size="xsmall"
          />
          <Badge
            text={`Option : ${valueRender(EnrollmentProperties.getEnrollmentOptions(enrollment).value)}`}
            variant="grey"
            size="xsmall"
          />

          <EnrollmentBadges t={t} size="xsmall" enrollment={enrollment} keys={['canGoOutAlone']} />
        </div>
      </div>

      <Modal
        hideHeader
        hideActions
        size={'medium'}
        isOpen={modalState['enrollment-image']}
        onCancel={() => toggleModal('enrollment-image')}
      >
        <img src={url ?? '/profile-default.jpg'} className="w-full h-full object-contain" />
      </Modal>
    </div>
  )
}

function EnrollmentsCardContactInformation({ enrollment }: EnrollmentsCardProps) {
  const { t } = useTranslate()
  const {
    parent1LastName,
    parent1FirstName,
    parent1PhoneNumber,
    parent1PhoneNumber2,
    parent2PhoneNumber,
    parent2LastName,
    parent2FirstName,
    personToContactInCaseOfEmergencyFullName,
    personToContactInCaseOfEmergencyPhoneNumber
  } = enrollment

  const parent2Gender = enrollment.parent2Gender ?? genderEnumValues.NONE
  const parent2Name = parent2LastName
    ? toFullName(parent2FirstName, parent2LastName, {
        options: { mode: 'lastname-firstname' }
      })
    : '-'

  const parent1Name = toFullName(parent1FirstName, parent1LastName, {
    options: { mode: 'lastname-firstname' }
  })

  return (
    <Box className="flex flex-col gap-2 bg-white-hover rounded-lg p-3 border border-grey-light">
      <div className="flex flex-row gap-2 items-center">
        <PeopleOneOutlineIcon color={tailwindColors.primary.DEFAULT} />
        <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
          {t('staff.personal.space.students.card.parents.contact')}
        </Typography>
      </div>

      <Badge
        variant="fushia"
        text={`${contactEnumMapping.PARENT_1} : ${genderOptionsMapping[enrollment.parent1Gender].title} ${parent1Name}`}
      />

      <div className="flex flex-row gap-0.5 items-center">
        <TelephoneIcon color={tailwindColors.grey.lighter} />
        <Typography variant="label-semibold">
          {`${valueRender(parent1PhoneNumber, (ph) => formatPhoneNumber(ph))}${parent1PhoneNumber2 ? ` - ${formatPhoneNumber(parent1PhoneNumber2)}` : ''}`}
        </Typography>
      </div>

      <Badge
        variant="blue"
        text={`${contactEnumMapping.PARENT_2} : ${parent2Gender === NoneEnum.NONE ? '' : genderOptionsMapping[parent2Gender].title} ${parent2Name}`}
      />

      <div className="flex flex-row gap-0.5 items-center">
        <TelephoneIcon color={tailwindColors.grey.lighter} />
        <Typography variant="label-semibold">
          {valueRender(parent2PhoneNumber, (ph) => formatPhoneNumber(ph))}
        </Typography>
      </div>

      <Badge
        borderColor={tailwindColors.grey.light.DEFAULT}
        textColor={tailwindColors.black.DEFAULT}
        text={`${t('staff.personal.space.students.card.emergency.contact')} : ${personToContactInCaseOfEmergencyFullName ?? '-'} ${valueRender(personToContactInCaseOfEmergencyPhoneNumber, (ph) => formatPhoneNumber(ph))}`}
      />
    </Box>
  )
}

function EnrollmentsCardHealthInformation({ enrollment }: EnrollmentsCardProps) {
  const { t } = useTranslate()
  const vaccinations = EnrollmentProperties.getEnrollmentVaccination(enrollment)
  const isRas = enrollment.medicalStatus === MedicalStatusEnum.RAS
  const isRepatriationInsurance = EnrollmentProperties.isRepatriationInsurance(enrollment)

  return (
    <Box className="flex flex-col gap-2 bg-white-hover rounded-lg p-3 border border-grey-light">
      <div className="flex flex-row gap-2 items-center">
        <HealthFormIcon color={tailwindColors.primary.DEFAULT} className="min-w-6 size-6" />
        <Typography
          variant="tag"
          className="whitespace-nowrap"
          color={tailwindColors.primary.DEFAULT}
        >
          {`${t('staff.personal.space.students.card.health.info')} :`}
        </Typography>

        <Badge
          variant={isRas ? 'grey' : 'danger'}
          text={isRas ? 'N/A' : valueRender(enrollment.medicalInformations)}
        />
      </div>

      <div className="flex flex-row gap-2 items-center">
        <PharmacyIcon color={tailwindColors.primary.DEFAULT} className="!size-5 shrink-0" />
        <Typography
          variant="tag"
          className="whitespace-nowrap"
          color={tailwindColors.primary.DEFAULT}
        >
          {`${t('staff.personal.space.students.card.insurance')} :`}
        </Typography>

        <Badge
          variant={isRepatriationInsurance ? 'success' : 'danger'}
          text={isRepatriationInsurance ? 'Oui' : 'Non'}
        />
      </div>

      <div className="flex flex-row gap-2 items-center">
        <div className="flex flex-wrap gap-1 items-center">
          <VaccinationIcon color={tailwindColors.primary.DEFAULT} className="min-w-6 size-6" />
          <Typography
            variant="tag"
            className="inline-block ml-1 whitespace-nowrap"
            color={tailwindColors.primary.DEFAULT}
          >
            {`${t('staff.personal.space.students.card.vaccination')} :`}
          </Typography>

          {!vaccinations || !vaccinations.length
            ? '-'
            : vaccinations.map((v) => (
                <Badge
                  key={v.key}
                  text={`${t(v.key as LocaleKeys)} ${DateFormatter.DDMMYYYY(v.value)}`}
                  size={'xsmall'}
                  variant="purple"
                  className="h-fit"
                />
              ))}
        </div>
      </div>
    </Box>
  )
}

function EnrollmentsCard({ enrollment }: EnrollmentsCardProps) {
  return (
    <Box style="shadow" className="mb-2 w-full">
      <EnrollmentsCardHeader enrollment={enrollment} />
      <EnrollmentsCardContactInformation enrollment={enrollment} />
      <EnrollmentsCardHealthInformation enrollment={enrollment} />
    </Box>
  )
}

type EnrollmentsAffectedStaffGridViewProps = {
  affectedStaffId: number
}

const LIMIT = 10

export function EnrollmentsAffectedStaffGridView({
  affectedStaffId
}: EnrollmentsAffectedStaffGridViewProps) {
  const { downloadDocuments } = useDownloadDocuments()
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslate()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey: ['affected-staffs', affectedStaffId, 'enrollments', LIMIT],
      queryFn: ({ pageParam = 0 }) =>
        getAffectedStaffEnrollments<'COMPACT'>(affectedStaffId, {
          limit: LIMIT,
          page: pageParam,
          version: 'COMPACT',
          sort: { enrollmentCenter: { centerId: 'asc' } }
        }),
      enabled: !!affectedStaffId,
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = allPages.length - 1
        const totalPages = Math.ceil(lastPage.total / LIMIT)
        if (currentPage >= totalPages - 1) return undefined
        return allPages.length
      },
      initialPageParam: 0
    })

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const currentLoadMoreRef = loadMoreRef.current

    if (currentLoadMoreRef) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) loadMore()
        },
        { threshold: 0.1 }
      )

      observerRef.current.observe(currentLoadMoreRef)
    }

    return () => {
      if (observerRef.current && currentLoadMoreRef)
        observerRef.current.unobserve(currentLoadMoreRef)
    }
  }, [loadMore])

  // Flatten all enrollments from all pages and group by center ID
  const enrollmentsByCenter = useMemo(() => {
    const allEnrollments = data?.pages.flatMap((page) => page.data) ?? []
    const grouped: Record<string, EnrollmentCompact[]> = {}

    allEnrollments.forEach((enrollment) => {
      const centerId = enrollment.enrollmentCenter?.center.id
      if (centerId) {
        if (!grouped[centerId]) grouped[centerId] = []

        grouped[centerId].push(enrollment)
      }
    })

    return grouped
  }, [data?.pages])

  // Get center names for display
  const getCenterName = (centerId: string): string => {
    const firstEnrollment = enrollmentsByCenter[centerId]?.[0]
    return firstEnrollment.enrollmentCenter?.center.name ?? `Center ${centerId}`
  }

  function getCenterDates(centerId: string): string | undefined {
    const firstEnrollment = enrollmentsByCenter[centerId]?.[0]
    const firstJourney = firstEnrollment?.enrollmentCenter?.center.centerToJourneys.at(0)
    const date = firstJourney?.journey.date
    if (!date) return undefined
    return `${DateFormatter.DDMMYYYY(date.departureDate)} - ${DateFormatter.DDMMYYYY(date.returnDate)}`
  }

  if (isLoading)
    return <div>{t('staff.personal.space.students.card.status.loading.enrollments')}</div>
  if (error)
    return <div>{t('staff.personal.space.students.card.status.error.loading.enrollments')}</div>

  return (
    <div className="flex flex-col gap-6">
      {/* Render enrollments grouped by center */}
      {Object.entries(enrollmentsByCenter).map(([centerId, enrollments]) => (
        <Box
          key={centerId}
          className={'border border-grey-light rounded-xl p-4 flex flex-col gap-3'}
        >
          <div className="flex flex-row gap-2 justify-between mobile:flex-col tab:flex-col">
            <div className="flex flex-row gap-2 justify-between items-center mobile:flex-col tab:flex-col mobile:items-start tab:items-start">
              <div className="flex items-center flex-row gap-2">
                <div className="p-1 bg-primary-light rounded">
                  <BuildingThreeIcon color={tailwindColors.grey.DEFAULT} />
                </div>

                <Typography variant={'sub-heading'} color={tailwindColors.primary.DEFAULT}>
                  {getCenterName(centerId)}
                </Typography>
              </div>

              <Badge variant="danger" text={getCenterDates(centerId)} />
            </div>

            <Button
              icon={PrintIcon}
              variant="ghost"
              onClick={() =>
                downloadDocuments({
                  ids: enrollments.map((e) => e.id),
                  endpoint: constructApiUrl(API.affectedStaffs.printEnrollmentsListWithPhoto.url, {
                    id: affectedStaffId
                  })
                })
              }
              label={t('staff.personal.space.students.table.actions.print')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 tab:grid-cols-1">
            {enrollments.map((enrollment) => (
              <EnrollmentsCard key={enrollment.id} enrollment={enrollment} />
            ))}
          </div>
        </Box>
      ))}

      {/* Loading indicator for next page */}
      {isFetchingNextPage && (
        <div className="p-5 text-center">
          {t('staff.personal.space.students.card.status.loading.more.enrollments')}
        </div>
      )}

      {/* Intersection observer target */}
      <div ref={loadMoreRef} className="h-2.5" />
    </div>
  )
}
